import type { JSX } from 'react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Ltr, Row, Spinner } from '../components/ui.js';
import type { AccessResponse } from '../lib/types.js';

export function AccessPage(): JSX.Element {
  const { t } = useI18n();
  const [project, setProject] = useState<string | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['access'],
    queryFn: () => api<AccessResponse>('/me/access'),
  });

  const projectCaps = useQuery({
    queryKey: ['access-project', project],
    queryFn: () => api<Record<string, unknown>>(`/me/access/project/${project}`),
    enabled: project !== null,
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }
  if (!data) return <Empty />;

  const jira = data.jiraCapabilities as Record<string, boolean | string | null>;

  return (
    <div className="page">
      <div className="header">
        <h1>{t('access.title').replace(/<[^>]+>/g, '')}</h1>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={isFetching}
          onClick={async () => {
            await api('/me/access/refresh', { method: 'POST' });
            void refetch();
          }}
        >
          🔄
        </button>
      </div>

      <Card title={t('admin.identity')}>
        <Row title={t('access.site')} right={data.identity.siteName ?? '—'} />
        <Row title={t('admin.account')} right={<Ltr>{data.identity.accountId ?? '—'}</Ltr>} />
        <Row title={t('admin.provider')} right={data.identity.provider} />
        <Row
          title={t('access.token_health', { status: '' }).replace(':', '')}
          right={
            <span className={`chip ${data.identity.connectionStatus === 'ACTIVE' ? 'chip-done' : 'chip-danger'}`}>
              {data.identity.connectionStatus}
            </span>
          }
        />
      </Card>

      <Card title={t('access.scopes', { count: data.oauthScopes.granted.length })}>
        <div className="note">{data.oauthScopes.note}</div>
        <div className="scroll-x" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          {data.oauthScopes.granted.length === 0 ? (
            <span className="muted small">{t('common.none')}</span>
          ) : (
            data.oauthScopes.granted.map((scope) => (
              <span key={scope} className="chip mono">
                {scope}
              </span>
            ))
          )}
        </div>
      </Card>

      <Card title="Jira">
        {Object.entries(jira)
          .filter(([key]) => key !== 'computedAt' && key !== 'assetsWorkspaceId')
          .map(([key, value]) => (
            <Row
              key={key}
              title={key}
              right={
                <span className={`chip ${value === true ? 'chip-done' : 'chip-new'}`}>
                  {value === true ? '✔' : '—'}
                </span>
              }
            />
          ))}
      </Card>

      <Card title="Bot">
        <Row title="Role" right={<span className="chip">{data.botPolicy.role}</span>} />
        {Object.entries(data.botPolicy.modules).map(([module, state]) => (
          <Row
            key={module}
            title={module}
            subtitle={state.locked ? t('common.locked') : state.source}
            right={
              <span className={`chip ${state.enabled ? 'chip-done' : 'chip-new'}`}>
                {state.enabled ? t('common.enabled') : t('common.disabled')}
              </span>
            }
          />
        ))}
      </Card>

      <Card title={t('access.projects', { count: data.projects.length })}>
        {data.projects.length === 0 ? <Empty /> : null}
        {data.projects.map((item) => (
          <Row
            key={item.key}
            onClick={() => setProject(item.key)}
            title={
              <>
                <Ltr>{item.key}</Ltr> · {item.name}
              </>
            }
            subtitle={item.typeKey ?? ''}
          />
        ))}
      </Card>

      {project && projectCaps.data ? (
        <Card title={project}>
          {Object.entries(projectCaps.data)
            .filter(([key]) => key.startsWith('can'))
            .map(([key, value]) => (
              <Row
                key={key}
                title={key}
                right={
                  <span className={`chip ${value === true ? 'chip-done' : 'chip-new'}`}>
                    {value === true ? '✔' : '—'}
                  </span>
                }
              />
            ))}
        </Card>
      ) : null}

      <div className="note">{t('access.explain_scopes')}</div>
    </div>
  );
}
